import { useState } from 'react'

import { NetworkStatus } from '@apollo/client'
import {
  Caption,
  SquareDotLoadingIndicator,
  TextButton,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import messages from 'features/Orders/Review/components/Overview/components/ProductsWidget/constants/messages'
import { FetchMoreFn } from 'features/Orders/Review/components/Overview/components/ProductsWidget/types'
import { PRODUCTS_LIMIT } from 'features/Orders/Review/review.queries'

const MAX_PRODUCTS_TO_CHUNK = 50

export function LoadAllProducts({ onLoadAllProds, total, count }: Props) {
  const [showError, setShowError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const showLoadMore = count < total
  const shouldRequestInChunks = total > MAX_PRODUCTS_TO_CHUNK

  type FetchMoreParams = {
    offset?: number
    limit?: number
  }

  const handleLoadAllProds = async () => {
    setIsLoading(true)
    try {
      const params: FetchMoreParams | undefined = shouldRequestInChunks
        ? { limit: PRODUCTS_LIMIT, offset: count }
        : undefined
      const { error, errors, networkStatus } = await onLoadAllProds(
        params || {},
      )
      if (error || errors || networkStatus === NetworkStatus.error) {
        throw new Error()
      }
    } catch (error) {
      console.error(
        '[OR23 OVERVIEW] > OrderReviewGigaQueryQuery> fetchMore',
        (error as Error).message,
      )
      setShowError(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <LoadAllProductsWrapper>
      <Caption type="2" typeface="montserrat" color={'primary-400'}>
        {messages.productsLoader.info({
          count,
          total,
        })}
      </Caption>
      {showError && (
        <Caption type="2" typeface="montserrat" color={'error-500'}>
          {messages.productsLoader.error}
        </Caption>
      )}
      {isLoading && (
        <LoadingWrapper>
          <LoaderStyled />
          <Caption type="2" typeface="montserrat" color={'primary-400'}>
            {shouldRequestInChunks
              ? messages.productsLoader.loadingMore
              : messages.productsLoader.loadingAll}
          </Caption>
        </LoadingWrapper>
      )}
      {!isLoading && showLoadMore && (
        <TextButton iconName="reset" onClick={handleLoadAllProds}>
          {shouldRequestInChunks
            ? messages.productsLoader.loadMore
            : messages.productsLoader.loadAll}
        </TextButton>
      )}
    </LoadAllProductsWrapper>
  )
}

type Props = {
  onLoadAllProds: FetchMoreFn
  total: number
  count: number
}

const LoadAllProductsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-block: 16px;

  button {
    &:last-child {
      font-size: 12px;
    }
  }
  p {
    margin: 0;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
  margin-left: -13px;

  p {
    margin-left: -8px;
  }
`

const LoaderStyled = styled(SquareDotLoadingIndicator)`
  transform: scale(0.5);
  > div {
    margin-top: 0 !important;
  }
`
